import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import { getAuthStorageKeys } from "@/lib/auth-storage";
import { readAuthenticatedSession, type AuthenticatedUser, type AuthenticatedSession } from "@/lib/authenticated-session";
import { refreshAccessToken } from "@/lib/axios";

export const AuthenticatedUserContext = createContext<{
  user: AuthenticatedUser | null; loading: boolean; error: boolean; retry: () => void;
  session?: AuthenticatedSession | null;
}>({ user: null, session: null, loading: true, error: false, retry: () => {} });

export function AuthenticatedUserProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthenticatedUser | null>(() => readAuthenticatedSession(getAuthStorageKeys().access)?.user || null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [session, setSession] = useState<AuthenticatedSession | null>(null);
  const [attempt, setAttempt] = useState(0);
  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(false);
    void Promise.resolve().then(async () => {
      if (!active) return;
      let current = readAuthenticatedSession(getAuthStorageKeys().access);
      if (!current) throw new Error("Missing authenticated user session");
      if (!current.authorization) throw new Error("Sign in again to verify this session's authorization scope.");
      if (current.authorization?.kind === "staff" && !current.permissions) {
        await refreshAccessToken(true);
        current = readAuthenticatedSession(getAuthStorageKeys().access);
        if (!current?.permissions) throw new Error("Permissions unavailable");
      }
      if (active) { setUser(current.user); setSession(current); }
    }).catch(() => { if (active) { setUser(null); setSession(null); setError(true); } })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [attempt]);
  return <AuthenticatedUserContext.Provider value={{ user, session, loading, error, retry: () => setAttempt((value) => value + 1) }}>
    {children}
  </AuthenticatedUserContext.Provider>;
}

export const useAuthenticatedUser = () => useContext(AuthenticatedUserContext);
